/**
 * Composable Report Widget shell.
 * Optional visualization + optional table; single dataset with limit/load-more driving both.
 */
import { Box, Skeleton, SxProps, Theme, Typography } from '@mui/material';
import { memo, useCallback, useMemo, useRef } from 'react';

import { headerHeightStyle } from 'shared/const/styles';

import { getChartColors } from './chartColors';
import { ComposableWidgetSideBySide } from './layout/ComposableWidgetSideBySide';
import { ComposableWidgetStacked } from './layout/ComposableWidgetStacked';

import { useComposableWidgetInteractions } from '../model/hooks/useComposableWidgetInteractions';
import { useComposableWidgetState } from '../model/hooks/useComposableWidgetState';

import type {
    ReportWidgetConfig,
    ReportWidgetItem,
    ReportWidgetTableConfig,
} from '../model/types';

interface ComposableReportWidgetProps {
    config: ReportWidgetConfig;
    items: ReportWidgetItem[];
    /** When provided, chart uses this instead of (limited) items. Use for e.g. pie with first N + "Other". */
    chartItems?: ReportWidgetItem[];
    loading?: boolean;
    isPrinting?: boolean;
    disableChartAnimation?: boolean;
    widgetSx?: SxProps<Theme>;
}

function titleToTestClass(title: string): string {
    return (
        title
            .trim()
            .toLowerCase()
            .replaceAll(/[^a-z0-9]+/g, '-')
            .replaceAll(/^-|-$/g, '') || 'composable-report-widget'
    );
}

export const ComposableReportWidget = memo(
    ({
        config,
        items,
        chartItems: chartItemsOverride,
        loading = false,
        isPrinting = false,
        disableChartAnimation = false,
        widgetSx = {},
    }: ComposableReportWidgetProps) => {
        const {
            widgetId,
            title,
            description,
            titleVariant = 'section',
            headerCountLabel,
            modelLabel,
            visualization,
            table: tableConfig,
            pagination,
            onRowClick,
            onChartItemClick,
            onDetailsClick,
        } = config;

        const table: ReportWidgetTableConfig = tableConfig ?? {
            enabled: false,
            columns: [],
        };

        const layout = config.layout ?? 'stacked';
        const isSideBySide = layout === 'sideBySide';

        const sectionRef = useRef<HTMLDivElement | null>(null);
        const {
            visibleItems,
            isInitialLoading,
            isLoadingMore,
            hasMore,
            loadMoreCount,
            handleLoadMore,
            canCollapse,
            handleCollapse,
        } = useComposableWidgetState({
            items,
            pagination,
            loading,
        });

        const handleCollapseAndScroll = useCallback(() => {
            handleCollapse();
            const section = sectionRef.current;
            if (!section) {
                return;
            }

            const headerOffset = Number.parseInt(headerHeightStyle, 10);
            if (section.getBoundingClientRect().top < headerOffset) {
                section.scrollIntoView({
                    block: 'start',
                    behavior: 'smooth',
                });
            }
        }, [handleCollapse]);

        const chartItems =
            chartItemsOverride ?? (table.enabled ? visibleItems : items);

        /** When chart + table both exist, omit the chart empty state; table shows a single no-data message. */
        const hideChartForEmptyData =
            visualization.enabled &&
            table.enabled &&
            chartItems.length === 0 &&
            !loading;

        const useSideBySideLayout =
            isSideBySide &&
            visualization.enabled &&
            table.enabled &&
            !hideChartForEmptyData;

        // Chart colors
        const paletteColors = useMemo(
            () =>
                getChartColors({
                    palette: visualization.options?.palette,
                    colors: visualization.options?.colors,
                }),
            [visualization.options?.palette, visualization.options?.colors],
        );

        const itemColors = useMemo(
            () =>
                visibleItems.map(
                    (item, i) =>
                        item.color ?? paletteColors[i % paletteColors.length],
                ),
            [visibleItems, paletteColors],
        );

        const {
            hoveredItemId,
            tableActiveItemId,
            handleRowClick,
            handleChartItemClick,
            handleDetailsClick,
            handleChartItemHover,
            handleTableRowHover,
            handleLoadMoreHover,
        } = useComposableWidgetInteractions({
            widgetId,
            chartItems,
            isSideBySide,
            syncHoverWithChart: visualization.enabled && table.enabled,
            onRowClick,
            onChartItemClick,
            onDetailsClick,
        });

        const chartClickHandler = onChartItemClick
            ? handleChartItemClick
            : undefined;

        return (
            <Box
                ref={sectionRef}
                data-testclass={
                    title
                        ? titleToTestClass(`report-${title}`)
                        : `composable-report-widget--${widgetId}`
                }
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: config.hasTopLegend ? '10px' : '18px',
                    overflow: 'visible',
                    boxSizing: 'border-box',
                    ...widgetSx,
                    scrollMarginTop: headerHeightStyle,
                }}
            >
                {title && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '16px',
                            marginBottom: description ? 0.5 : 0,
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="h2"
                                sx={
                                    titleVariant === 'section'
                                        ? { color: 'common.black' }
                                        : {
                                              fontWeight: 500,
                                              fontSize: '2rem',
                                              color: 'common.black',
                                          }
                                }
                            >
                                {title}
                            </Typography>
                            {description && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        marginTop: 0.5,
                                    }}
                                >
                                    {description}
                                </Typography>
                            )}
                        </Box>
                        {headerCountLabel != null &&
                            (isInitialLoading ? (
                                <Skeleton
                                    variant="text"
                                    data-testclass="report-widget-header-count-loading"
                                    sx={{
                                        flexShrink: 0,
                                        width: '120px',
                                        height: '2.2rem',
                                        transform: 'none',
                                    }}
                                />
                            ) : (
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: 'common.black',
                                        flexShrink: 0,
                                    }}
                                    data-testclass="report-widget-header-count"
                                >
                                    {headerCountLabel}
                                </Typography>
                            ))}
                    </Box>
                )}

                {useSideBySideLayout ? (
                    <ComposableWidgetSideBySide
                        chartItems={chartItems}
                        visibleItems={visibleItems}
                        visualization={visualization}
                        table={table}
                        modelLabel={modelLabel}
                        loading={loading}
                        isPrinting={isPrinting}
                        disableChartAnimation={disableChartAnimation}
                        isInitialLoading={isInitialLoading}
                        isLoadingMore={isLoadingMore}
                        hasMore={hasMore}
                        handleLoadMore={handleLoadMore}
                        loadMoreCount={loadMoreCount}
                        canCollapse={canCollapse}
                        handleCollapse={handleCollapseAndScroll}
                        itemColors={itemColors}
                        rowClickHandler={
                            onRowClick ? handleRowClick : undefined
                        }
                        detailsClickHandler={handleDetailsClick}
                        chartItemClickHandler={chartClickHandler}
                        hoveredItemId={hoveredItemId}
                        tableActiveItemId={tableActiveItemId}
                        chartHoverHandler={handleChartItemHover}
                        tableRowHoverHandler={handleTableRowHover}
                        loadMoreHoverHandler={handleLoadMoreHover}
                    />
                ) : (
                    <ComposableWidgetStacked
                        chartItems={chartItems}
                        visibleItems={visibleItems}
                        visualization={visualization}
                        table={table}
                        modelLabel={modelLabel}
                        showChart={
                            visualization.enabled && !hideChartForEmptyData
                        }
                        loading={loading}
                        isPrinting={isPrinting}
                        disableChartAnimation={disableChartAnimation}
                        isInitialLoading={isInitialLoading}
                        isLoadingMore={isLoadingMore}
                        hasMore={hasMore}
                        handleLoadMore={handleLoadMore}
                        loadMoreCount={loadMoreCount}
                        canCollapse={canCollapse}
                        handleCollapse={handleCollapseAndScroll}
                        itemColors={itemColors}
                        rowClickHandler={
                            onRowClick ? handleRowClick : undefined
                        }
                        detailsClickHandler={handleDetailsClick}
                        chartItemClickHandler={chartClickHandler}
                        hoveredItemId={hoveredItemId}
                        tableActiveItemId={tableActiveItemId}
                        chartHoverHandler={handleChartItemHover}
                        tableRowHoverHandler={handleTableRowHover}
                    />
                )}
            </Box>
        );
    },
);

ComposableReportWidget.displayName = 'ComposableReportWidget';
